import type { HeadersFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import cx from 'classnames';

import Page from '~/components/Layout/Page';
import type { PostCardStyles } from '~/components/PostCardv3/types';
import type { SignupStyles } from '~/components/Signup/types';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import useWebBuilderCommunicator from '~/hooks/useUtils/useWebBuilderCommunicator';
import remixI18n from '~/i18next.server';
import type { HomePage } from '~/interfaces/home_page';
import type { PaginatedPosts, Post } from '~/interfaces/post';
import type { Publication } from '~/interfaces/publication';
import getCardStyles from '~/pages/index/_utils/getCardStyles';
import getSignupWidgetConfigs from '~/pages/index/_utils/getSignupWidgetConfigs';
import ArchiveSection from '~/pages/index/ArchiveSection';
import FeaturedSections from '~/pages/index/FeaturedSections';
import Header from '~/pages/index/Header';
import HomePagev2 from '~/pages/index/HomePagev2';
import PrivatePublication from '~/pages/index/PrivatePublication';
import WidgetsSection from '~/pages/index/WidgetsSection';
import getHomePage from '~/services/api/home_pages/getHomePage.server';
import getFeaturedPosts from '~/services/api/posts/getFeaturedPosts.server';
import getPaginatedPosts from '~/services/api/posts/getPaginatedPosts.server';
import getPublication from '~/services/api/publication/getPublication.server';
import getWebThemev2 from '~/services/api/web_templates/getWebThemev2.server';
import * as posts from '~/services/exampleSite/archivePosts';
import getExampleHomePage from '~/services/exampleSite/getExampleHomePage.server';
import getExamplePublication from '~/services/exampleSite/getExamplePublication.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import previewAllowedCheck from '~/utils/previewAllowedCheck';
import redirectWithToast from '~/utils/redirectWithToast/index.server';
import setCacheHeaders from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const meta: MetaFunction<typeof loader> = ({ matches, data, location }) => {
  const metaTitle = data?.metaTitle;
  const metaDescription = data?.metaDescription;
  const links: any = [];
  const requestUrl = data?.requestUrl;
  const schemaUrl = getCanonicalUrl(requestUrl);
  const renderableFonts = data?.homePage?.renderable_fonts || [];

  if (renderableFonts.length > 0) {
    renderableFonts.forEach((font: string) => {
      links.push({
        rel: 'stylesheet',
        href: font,
        tagName: 'link',
      });
    });
  }

  if (data?.homePage?.main_bg_image_url) {
    links.push({
      rel: 'preload',
      href: data?.homePage?.main_bg_image_url,
      as: 'image',
    });
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(requestUrl),
    matches,
    overrides: {
      title: metaTitle,
      description: metaDescription,
    },
    links,
    structuredData: {
      url: schemaUrl,
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${schemaUrl}?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
  });
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const headers: HeadersFunction = ({ actionHeaders, loaderHeaders, parentHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': 'orchid-page',
  };
};

// biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
export const loader = async ({ request, params }: any) => {
  const { session } = await loaderSession(request);
  const url = new URL(request?.url);
  const searchParams = url.searchParams;
  const isDraft = searchParams.get('version') === 'draft';
  const isExampleSite = searchParams.get('example_site') === 'true';
  const showExampleSite = isDraft && isExampleSite;
  const forceNewWebBuilder = searchParams.get('new_web_builder') === 'true';

  const { instanceToFetch, isInitialLoadFromSwarm } = previewAllowedCheck(request);

  const [paginatedPosts, publication, featuredPosts, homePage, webTheme] = await Promise.all([
    getPaginatedPosts(request, session),
    getPublication(request, session),
    getFeaturedPosts(request, session),
    getHomePage(request, session, {
      status: instanceToFetch,
    }),
    getWebThemev2(request, session, {
      status: instanceToFetch,
    }),
  ]);

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  if (url.searchParams.get('upgrade') === 'success') {
    return redirect(
      redirectWithToast({
        baseUrl: '/',
        toast: 'success',
        title: t('upgrade.success.title') || '',
        message: t('upgrade.success.message'),
      }),
    );
  }

  // Handle redirection logic when no posts are found for a publication
  const hasNoPosts = !publication.has_live_web_posts;
  const hasPostSubscribeForm = publication?.post_subscribe_form_id;
  const email = url.searchParams.get('email');
  const shouldRedirectWithEmail = hasPostSubscribeForm && email;
  const isPrivate = publication.private;

  const shouldRedirectToSubscribe = !isPrivate && hasNoPosts && !isDraft;

  if (shouldRedirectToSubscribe) {
    let path = '/subscribe';

    if (shouldRedirectWithEmail) {
      path = `/subscribe?email=${email}`;
    }

    return redirect(path);
  }

  // We want users to be able to update their publication's design in the DesignLab.
  if (isInitialLoadFromSwarm) {
    publication.private = false;
  }

  return json(
    {
      requestUrl: url.href,
      showExampleSite,
      cardStyles: getCardStyles({
        homePage,
        webTheme: webTheme.web_theme,
      }),
      signupWidgetConfigs: getSignupWidgetConfigs({
        webTheme: webTheme.web_theme,
        overrides: {
          buttonBgColor: homePage?.main_section.signup_widget.bg_color,
          buttonTextColor: homePage?.main_section.signup_widget.text_color,
          fontFamily: homePage?.main_section.signup_widget.font,
          buttonText: homePage?.main_section.signup_widget.cta_text,
          placeholderText: homePage?.main_section.signup_widget.placeholder_text,
          borderColor: homePage?.main_section.signup_widget.border_color,
        },
      }),
      paginatedPosts: showExampleSite ? posts.paginatedPosts : paginatedPosts,
      publication: showExampleSite ? getExamplePublication(publication) : publication,
      featuredPosts: showExampleSite ? posts.featuredPosts : featuredPosts,
      homePage: showExampleSite ? getExampleHomePage(homePage) : homePage,
      metaTitle: homePage?.seo_section?.title || publication.name || '',
      metaDescription: homePage?.seo_section?.description || publication.description || '',
      isInitialLoadFromSwarm,
      hasWebThemesEnabled: webTheme?.feature_enabled || forceNewWebBuilder,
      isDraft,
    },
    {
      // Note: Keep MaxAge 0 to avoid caching in the browser, we only want to cache via the CDN with sMaxAge.
      headers: setCacheHeaders({
        maxAge: 0,
        sMaxAge: 60 * 60 * 24 * 30, // 30 days
        staleWhileRevalidate: 60,
        publication: { private: isPrivate },
      }),
    },
  );
};

export interface HomePageLoaderData {
  cardStyles: PostCardStyles;
  signupWidgetConfigs: SignupStyles;
  showExampleSite: boolean;
  paginatedPosts: PaginatedPosts;
  publication: Publication;
  featuredPosts: Post[];
  homePage: HomePage;
  isInitialLoadFromSwarm: boolean;
  metaTitle: string;
  metaDescription: string;
  requestUrl: string;
  hasWebThemesEnabled: boolean;
  isDraft: boolean;
}

const Index = () => {
  const { homePage, publication, isInitialLoadFromSwarm, showExampleSite, hasWebThemesEnabled, isDraft } =
    useTypedLoaderData<HomePageLoaderData>();

  // Throw RefreshPageError if loader data is invalid [WEB-5443]
  if (!publication) {
    throw new Error('RefreshPageError');
  }

  useWebBuilderCommunicator({
    isInitialLoadFromSwarm,
    publicationId: publication.id,
  });

  const hasWebBuilderV2 = hasWebThemesEnabled;
  const hasWidgetsEnabled = homePage?.widgets_section?.widgets_enabled?.length > 0;
  const archiveLayout = homePage?.posts_section?.archive.layout;

  const isLoggedIn = useIsLoggedIn();
  const isPrivate = (publication.private || publication.require_subscriber_approval) && !isLoggedIn;

  if (!isDraft && isPrivate) {
    return <PrivatePublication publication={publication} />;
  }

  if (hasWebBuilderV2) {
    return <HomePagev2 homePage={homePage} showExampleSite={showExampleSite} />;
  }

  return (
    <Page bgColor={homePage.general_section.theme.bg_color} className="pb-8">
      <Header />
      {/** Removing will break some enterprise user's programatic ads */}
      <div className="w-full max-w-6xl mx-auto">
        <div id="header-ad-placement" />
      </div>
      {/*-------------------------------------------------------------*/}

      <div className="px-4">
        <div className="w-full max-w-6xl mx-auto">
          <FeaturedSections postsSection={homePage.posts_section} generalSection={homePage.general_section} />
        </div>
      </div>

      <div className="px-4">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid w-full grid-cols-12">
            <div
              className={cx({
                'order-last col-span-12 py-2 lg:order-first': true,
                'pr-0 lg:col-span-8 lg:pr-12': hasWidgetsEnabled,
                'mb-8': !hasWidgetsEnabled,
              })}
            >
              <ArchiveSection
                gridCols={hasWidgetsEnabled ? '2' : '3'}
                cardOrientation={archiveLayout === 'list' ? 'horizontal' : 'vertical'}
              />
            </div>

            {hasWidgetsEnabled && (
              <div className="col-span-12 pt-4 pl-0 pr-0 lg:col-span-4 lg:pl-8 lg:pt-8">
                <WidgetsSection />

                {/** Removing will break some enterprise user's programatic ads */}
                <div id="side-panel-ad-placement" />
                {/*-------------------------------------------------------------*/}
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export const shouldRevalidate = shouldRevalidateLoaderData;

export default Index;
